<script>
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import MetricCard from '../components/MetricCard.svelte';
  import DataTable from '../components/DataTable.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  let loading = $state(true);
  let error = $state(null);
  let performance = $state(null);
  let days = $state(30);

  async function load() {
    loading = true;
    error = null;
    try {
      performance = await api.getPerformance({ days, tenant_id: authStore.tenantId ?? undefined });
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    days;
    load();
  });

  $effect(() => {
    const onTenantChange = () => load();
    window.addEventListener('strao:tenant-changed', onTenantChange);
    return () => window.removeEventListener('strao:tenant-changed', onTenantChange);
  });

  const totals = $derived(performance?.totals ?? {});
  const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
  const num = (v) => (v ?? 0).toLocaleString();

  const columns = [
    { key: 'property_name', label: 'Property',
      render: (r) => r.property_name ?? 'Unlinked' },
    { key: 'messages_analyzed', label: 'Messages', align: 'right', render: (r) => num(r.messages_analyzed) },
    { key: 'auto_sent', label: 'Auto-sent', align: 'right', render: (r) => num(r.auto_sent) },
    { key: 'suggested', label: 'For review', align: 'right', render: (r) => num(r.suggested) },
    { key: 'escalated', label: 'Escalated', align: 'right', render: (r) => num(r.escalated) },
    { key: 'pending_review', label: 'Awaiting host', align: 'right', render: (r) => num(r.pending_review) },
    { key: 'automation_rate', label: 'Automation', align: 'right', render: (r) => pct(r.automation_rate) },
  ];
</script>

<PageHeader title="Dashboard"
            subtitle="Auto-responder activity across your properties.">
  {#snippet children()}
    <select class="p-input w-auto text-sm" bind:value={days} aria-label="Time window">
      <option value={7}>Last 7 days</option>
      <option value={30}>Last 30 days</option>
      <option value={90}>Last 90 days</option>
    </select>
  {/snippet}
</PageHeader>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else}
  <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
    <MetricCard label="Messages" value={num(totals.messages_analyzed)} {loading} />
    <MetricCard label="Auto-sent" value={num(totals.auto_sent)} {loading} tone="success" />
    <MetricCard label="For review" value={num(totals.suggested)} {loading} />
    <MetricCard label="Escalated" value={num(totals.escalated)} {loading} tone="warning" />
    <MetricCard label="Awaiting host" value={num(totals.pending_review)} {loading}
                tone={totals.pending_review ? 'warning' : 'default'} />
  </div>

  <h2 class="mb-3 mt-8 text-sm font-semibold">By property</h2>
  {#if !loading && !performance?.by_property?.length}
    <EmptyState icon="fa-chart-simple" title="No activity in this window"
                message="Once guests start messaging, per-property results appear here." />
  {:else}
    <DataTable {columns} rows={performance?.by_property ?? []} {loading}
               rowKey={(r) => r.property_id ?? 'unlinked'} />
  {/if}
{/if}
